// Getters do slice `journey`. Todos retornam um valor default (nunca undefined).
import { GUIDE_ORIGIN_JOURNEY } from '@modules/central-config/utils/guide-origin'
import {
  essentialTasksNaOrdemVisual,
  isEssential,
  tasksNaOrdemVisual,
} from '@modules/central-config/utils/progress'

// ---- Nível (spec-004) ----
// `nivelAtual` espelha o que o backend resolveu; o front NUNCA promove sozinho.
// O default espelha `state.nivelAtual`, que é só `{ nivel }`: o
// `progressoPorNivel` saiu do state junto com o getter que o lia — hoje o
// progresso por nível é derivado do catálogo VISÍVEL, no
// `journeyVisibilityMixin` (`progressoPorNivelVisivel`), porque contar o que a
// tela escondeu era o que fazia o rail divergir da listagem.
export const nivelAtual = (state) => {
  return state.nivelAtual || { nivel: 1 }
}

export const nivelAtualNumero = (state) => {
  return (state.nivelAtual && state.nivelAtual.nivel) || 1
}

export const journey = (state) => {
  return (
    state.journey.data || {
      workspace: { office: '', professional: '' },
      generatedAt: '',
      modules: [],
    }
  )
}

export const journeyModules = (state) => {
  return (state.journey.data && state.journey.data.modules) || []
}

// Contrato: workspace é um objeto { office, professional } (não string).
export const journeyWorkspace = (state) => {
  return (
    (state.journey.data && state.journey.data.workspace) || {
      office: '',
      professional: '',
    }
  )
}

export const journeyLoading = (state) => {
  return state.journey.loading
}

export const journeyLastUpdate = (state) => {
  return state.journey.lastUpdate
}

// Getters do slice `guide` (drawer). Todos retornam valor default (nunca undefined).

export const guideOpen = (state) => {
  return !!(state.guide && state.guide.open)
}

export const guideCollapsed = (state) => {
  return !!(state.guide && state.guide.collapsed)
}

// Painel lateral visível: guia ativo E não recolhido.
export const guidePanelOpen = (state) => {
  return !!(state.guide && state.guide.open && !state.guide.collapsed)
}

// FAB (bolinha) visível: guia ativo E recolhido.
export const guideFabOpen = (state) => {
  return !!(state.guide && state.guide.open && state.guide.collapsed)
}

export const guideModuleId = (state) => {
  return (state.guide && state.guide.moduleId) || null
}

export const guideTaskId = (state) => {
  return (state.guide && state.guide.taskId) || null
}

// Origem da sessão do guia — 'journey' (default) ou 'module'. Ver
// utils/guide-origin.js. O default aqui é a 3ª camada de fail-open (as outras
// duas são SET_GUIDE e readGuide): um store de teste que não declare este getter
// devolve undefined, e o `||` garante o comportamento antigo.
export const guideOrigin = (state) => {
  return (state.guide && state.guide.origin) || GUIDE_ORIGIN_JOURNEY
}

// `slug` do módulo de origem (não o UUID) — chave de MODULE_KEY_TO_ROUTE.
export const guideModuleKey = (state) => {
  return (state.guide && state.guide.moduleKey) || null
}

// Getters do slice `stepTransition` — transição "Iniciar etapa".
// Todos com valor default (nunca undefined).

export const navigatingTaskId = (state) => {
  return (state.stepTransition && state.stepTransition.navigatingTaskId) || null
}

export const exitingModuleId = (state) => {
  return (state.stepTransition && state.stepTransition.exitingModuleId) || null
}

// Resolve os pré-requisitos de uma tarefa (nome + se está concluída) dentro do
// mesmo módulo. Extraído para manter guideContext dentro do limite de complexidade.
function resolveRequires(task, tasks) {
  const requires =
    task.metadata && Array.isArray(task.metadata.requires)
      ? task.metadata.requires
      : []
  return requires.map((requiredId) => {
    const required = tasks.find((t) => t.id === requiredId) || null
    const status =
      (required && required.metadata && required.metadata.status) || 'todo'
    return {
      id: requiredId,
      name: (required && required.name) || requiredId,
      isDone: status === 'done',
      exists: !!required,
    }
  })
}

// Localiza { module, tasks, index } do guia atual. Retorna null quando ausente.
// Separado de guideContext para manter cada função dentro do limite de complexidade.
function locateGuideTask(state) {
  const modules = (state.journey.data && state.journey.data.modules) || []
  const guide = state.guide || {}
  if (!guide.moduleId || !guide.taskId) return null

  const module = modules.find((m) => m.id === guide.moduleId) || null
  if (!module) return null

  const tasks = (Array.isArray(module.tasks) && module.tasks) || []
  const index = tasks.findIndex((t) => t.id === guide.taskId)
  if (index < 0) return null

  return { module, tasks, index }
}

// Resolve o contexto completo do guia a partir de journeyModules + moduleId/taskId.
// Retorna null quando o módulo/tarefa não existe (jornada ainda não carregada ou
// ids inválidos) — o drawer trata null como "sem conteúdo".
export const guideContext = (state) => {
  const located = locateGuideTask(state)
  if (!located) return null

  const { module, tasks, index } = located
  const total = tasks.length
  const task = tasks[index]
  // Posição da etapa DENTRO do seu grupo (obrigatórias vs opcionais) — o drawer
  // mostra "Etapa X de Y" por grupo, não sobre o total do módulo. As
  // obrigatórias são ordenadas pela ORDEM VISUAL do accordion (trilhas +
  // sem-trilha por último — `essentialTasksNaOrdemVisual`), para o contador
  // bater com o que o usuário vê na tela: no CRM "Conectar e-mail" é a 1ª da
  // trilha "Ferramentas" e mostra "1 de 21", não "10 de 21" (posição crua).
  const essentialTasks = essentialTasksNaOrdemVisual(module)
  const optionalTasks = tasks.filter((t) => !isEssential(t))
  // ★ Navegação prev/next GLOBAL segue a ORDEM VISUAL completa (2026-08-05,
  // fix do achado do usuário): `tasksNaOrdemVisual` = obrigatórias na ordem das
  // trilhas + opcionais na ordem crua, uma lista única que cruza a fronteira
  // obrigatória/opcional — exatamente o que o drawer mostra de cima para baixo.
  // Antes `prevTask`/`nextTask` andavam pelo array CRU do catálogo (ordem do
  // drag-and-drop do Interno), que pode divergir da trilha — no Financial
  // Planning "Conectar contas" é a 1ª da trilha "Planejamento Orçamentário" mas
  // não necessariamente a 1ª crua, e "Próxima etapa" pulava para a etapa errada.
  // `index`/`total` seguem crus (módulo inteiro): nada do front consome a
  // posição crua hoje, e mantê-los evita quebrar o contrato do getter.
  const visualTasks = tasksNaOrdemVisual(module)
  const visualIndex = visualTasks.findIndex((t) => t.id === task.id)
  return {
    module,
    task,
    index,
    total,
    essentialIndex: essentialTasks.findIndex((t) => t.id === task.id),
    essentialTotal: essentialTasks.length,
    optionalIndex: optionalTasks.findIndex((t) => t.id === task.id),
    optionalTotal: optionalTasks.length,
    prevTask: visualIndex > 0 ? visualTasks[visualIndex - 1] : null,
    nextTask:
      visualIndex >= 0 && visualIndex + 1 < visualTasks.length
        ? visualTasks[visualIndex + 1]
        : null,
    requiresTasks: resolveRequires(task, tasks),
  }
}

export const returnToModuleId = (state) => state.returnToModuleId || null

// Etapa de origem do retorno — define o nível a reabrir quando o módulo
// aparece em mais de uma seção (ver `restaurarPosicao` na home).
export const returnToTaskId = (state) => state.returnToTaskId || null

// ---- Tour guiado (jornada guiada) ----
// Todos com default — nunca undefined, como o resto do módulo.

export const tourAtivo = (state) => {
  return !!(state.tour && state.tour.ativo)
}

export const tourContexto = (state) => {
  return (
    state.tour || {
      ativo: false,
      moduleId: null,
      moduleKey: null,
      taskId: null,
      taskSlug: null,
      passos: [],
      proximaEtapa: null,
      indice: 0,
      fase: 'passos',
      recolheuGuia: false,
      marcarConcluida: true,
    }
  )
}

// `tourPassos` foi removido em 14/08: nasceu junto com o slice e nunca teve
// consumidor — quem lê os passos é `tourContexto`, que já devolve o objeto
// inteiro. Getter sem consumidor é superfície que alguém vai usar por engano
// achando que é o caminho oficial.
